/** User administration endpoints (`/users`, ADMIN only). */
import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { deleteData, getList, patchData, postData } from "../lib/apiClient";
import { getData } from "../lib/apiClient";
import { queryKeys } from "./queryKeys";
import { Role, User } from "../types/models";
import { ListParams } from "../types/api";

export interface ListUsersParams extends ListParams {
  role?: Role;
  isActive?: boolean;
}

export interface CreateUserInput {
  email: string;
  password: string;
  fullName: string;
  phone?: string;
  /** Any internal role; CLIENT accounts are created via self-registration. */
  role: Exclude<Role, "CLIENT">;
}

export interface UpdateUserInput {
  fullName?: string;
  phone?: string;
  role?: Exclude<Role, "CLIENT">;
  isActive?: boolean;
}

export const usersApi = {
  list: (params?: ListUsersParams) => getList<User>("/users", params),
  getById: (id: string) => getData<User>(`/users/${id}`),
  create: (input: CreateUserInput) => postData<User>("/users", input),
  update: (id: string, input: UpdateUserInput) =>
    patchData<User>(`/users/${id}`, input),
  setActive: (id: string, isActive: boolean) =>
    patchData<User>(`/users/${id}/status`, { isActive }),
  resetPassword: (id: string, newPassword: string) =>
    postData<null>(`/users/${id}/reset-password`, { newPassword }),
  remove: (id: string) => deleteData<null>(`/users/${id}`),
};

// ── Hooks ────────────────────────────────────────────────────────────

export const useUsers = (params?: ListUsersParams) =>
  useQuery({
    queryKey: queryKeys.users.list(params),
    queryFn: () => usersApi.list(params),
  });

export const useUser = (id: string | undefined) =>
  useQuery({
    queryKey: queryKeys.users.detail(id ?? ""),
    queryFn: () => usersApi.getById(id as string),
    enabled: Boolean(id),
  });

export const useCreateUser = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateUserInput) => usersApi.create(input),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.users.all }),
  });
};

export const useUpdateUser = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateUserInput }) =>
      usersApi.update(id, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.users.all }),
  });
};

export const useSetUserActive = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      usersApi.setActive(id, isActive),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.users.all }),
  });
};

export const useResetUserPassword = () =>
  useMutation({
    mutationFn: ({ id, newPassword }: { id: string; newPassword: string }) =>
      usersApi.resetPassword(id, newPassword),
  });
